/**
 * SF-4656 — Active, Account Type and Sales Channel, changed straight from the
 * company page's General Info tab (they used to need the Edit form, which no
 * longer has them). Each change saves on its own through the page's
 * single-field saver (useSaveCompanyField), so the new value shows at once.
 *
 *  - Active: switching a company to INACTIVE asks first; switching it back on
 *    saves at once.
 *  - Sales Channel: a company saved before SF-4578 has none stored and shows
 *    Enterprise (the display fallback). Nothing is saved until staff actually
 *    pick a value, so an online sign-up is never locked in as enterprise
 *    ahead of the SF-4578 backfill (which never overwrites a stored value).
 *
 * Changing any of them needs admin:companies:write (Refine companies:edit).
 * Without it the values show as read-only tags, as before.
 */
import { Select, Tag, Tooltip } from 'antd';
import {
  ACCOUNT_TYPE_PICKER_OPTIONS,
  SALES_CHANNELS,
  SALES_CHANNEL_LABELS,
  accountTypeErrorText,
  accountTypeLabel,
  salesChannelLabel,
  type AccountType,
  type SalesChannel,
} from '../constants';
import { FlagSwitch } from './FlagSwitch';
import type { PendingSave, SaveFieldOptions } from './useSaveCompanyField';
import type { Company } from '../list';

const SALES_CHANNEL_OPTIONS = SALES_CHANNELS.map((c) => ({ label: SALES_CHANNEL_LABELS[c], value: c }));

export const ACCOUNT_TYPE_TOOLTIP =
  "Demo = sales demo. Test = only exists to test features. ServeFirst Live = ServeFirst's own working company (only one).";
export const SALES_CHANNEL_TOOLTIP = 'Self-serve = signed up and paid online.';

export interface InlineSettingProps {
  company: Company;
  canEdit: boolean;
  saver: {
    save: <K extends keyof Company>(
      field: K,
      value: Company[K],
      successText: string,
      options?: SaveFieldOptions,
    ) => Promise<boolean>;
    pending: PendingSave | null;
    busy: boolean;
  };
}

const pendingOf = (saver: InlineSettingProps['saver'], field: keyof Company) =>
  saver.pending?.field === field ? saver.pending.value : null;

export const ActiveSetting: React.FC<InlineSettingProps> = ({ company, canEdit, saver }) => {
  const active = company.is_active === true;
  if (!canEdit) {
    return <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>;
  }
  return (
    <FlagSwitch
      testId="company-active-toggle"
      ariaLabel="Active"
      checked={active}
      pendingValue={pendingOf(saver, 'is_active') as boolean | null}
      disabled={saver.busy}
      danger
      confirmOff={{
        title: 'Make this company inactive?',
        description: 'The company is marked inactive. You can switch it back on at any time.',
        okText: 'Make inactive',
      }}
      onChange={(next) =>
        void saver.save('is_active', next, next ? 'Company is now active' : 'Company is now inactive')
      }
    />
  );
};

const ACCOUNT_TYPE_SAVE_OPTIONS: SaveFieldOptions = { errorText: accountTypeErrorText };

export const AccountTypeSetting: React.FC<InlineSettingProps> = ({ company, canEdit, saver }) => {
  if (!canEdit) {
    return company.account_type ? (
      <Tag color="blue">{accountTypeLabel(company.account_type)}</Tag>
    ) : (
      <>—</>
    );
  }
  const pending = pendingOf(saver, 'account_type') as AccountType | null;
  return (
    <Tooltip title={ACCOUNT_TYPE_TOOLTIP} placement="right">
      <Select<AccountType>
        aria-label="Account Type"
        data-testid="company-account-type"
        options={ACCOUNT_TYPE_PICKER_OPTIONS}
        value={pending ?? company.account_type}
        placeholder="—"
        loading={pending !== null}
        disabled={saver.busy}
        style={{ width: 200 }}
        onChange={(next) => {
          if (next !== company.account_type) {
            void saver.save(
              'account_type',
              next,
              `Account type set to ${accountTypeLabel(next)}`,
              ACCOUNT_TYPE_SAVE_OPTIONS,
            );
          }
        }}
      />
    </Tooltip>
  );
};

export const SalesChannelSetting: React.FC<InlineSettingProps> = ({ company, canEdit, saver }) => {
  if (!canEdit) return <Tag>{salesChannelLabel(company.sales_channel)}</Tag>;
  const pending = pendingOf(saver, 'sales_channel') as SalesChannel | null;
  const stored = company.sales_channel ?? null;
  return (
    <Tooltip title={SALES_CHANNEL_TOOLTIP} placement="right">
      <Select<SalesChannel>
        aria-label="Sales Channel"
        data-testid="company-sales-channel"
        options={SALES_CHANNEL_OPTIONS}
        // Display fallback only (pre-SF-4578 record); never saved by itself.
        value={pending ?? stored ?? 'enterprise'}
        loading={pending !== null}
        disabled={saver.busy}
        style={{ width: 200 }}
        // onSelect, not onChange: picking the shown fallback (Enterprise on a
        // company with nothing stored) must still save it, and onChange does
        // not fire when the picked value equals the shown one.
        onSelect={(next: SalesChannel) => {
          if (next !== stored) {
            void saver.save('sales_channel', next, `Sales channel set to ${SALES_CHANNEL_LABELS[next]}`);
          }
        }}
      />
    </Tooltip>
  );
};
